import type { Root } from "./prototypeTypes";
import type { MediaSurface, MountedPlayer } from "./mediaPlayerTypes";
import { activeVideoDurationFallback, restoreVideoPosition, runtimeOf, syncMediaUi } from "./mediaPlayerProgress";
import { releaseMediaLeaseBestEffort } from './mediaLeaseLifecycle';
import { updateVideoWatermarkTrace } from './videoWatermark';
import { cancelVideoPictureInPictureExitHandoff, isVideoPictureInPictureActive } from './videoPictureInPicture';

export function tryReusePictureInPictureVideo(
  root: Root,
  media: MountedPlayer | undefined,
  surface: MediaSurface,
  options: { topicId?: string; chapterIndex?: number; deferExistingLeaseRelease?: boolean } = {},
): boolean | null {
  if (
    !media
    || surface.type !== "video"
    || media.kind !== "video"
    || !(media.element instanceof HTMLVideoElement)
    || !isVideoPictureInPictureActive(media.element)
  ) {
    return null;
  }
  const rt = runtimeOf(root);
	// A PiP surface keeps the same <video>. Invalidate an in-flight refresh
	// before this newer chapter source is written to that shared element.
	media.videoSourceOperationGeneration = (media.videoSourceOperationGeneration ?? 0) + 1;
	media.videoStallRecoveryCleanup?.();
	if (media.videoStallRecoveryTimer !== undefined) clearTimeout(media.videoStallRecoveryTimer);
	media.videoStallRecoveryTimer = undefined;
	media.videoStallRecoveryCleanup = undefined;
	const previousUrl = media.url;
	try {
		cancelVideoPictureInPictureExitHandoff(media.element);
		media.element.src = surface.signedPlaybackUrl;
    media.element.load();
  } catch {
    media.element.src = previousUrl;
    try { media.element.load(); } catch { /* The existing PiP surface remains the active identity. */ }
    return false;
  }
  if (!options.deferExistingLeaseRelease) releaseMediaLeaseBestEffort(media);
  media.url = surface.signedPlaybackUrl;
  media.topicId = options.topicId ?? rt?.activeCourseId;
  media.chapterIndex = options.chapterIndex ?? rt?.activeChapterIndex;
  media.expiresAt = Math.floor(Date.now() / 1000) + surface.expiresInSeconds;
  media.playbackLeaseId = surface.playbackLeaseId;
  media.heartbeatIntervalSeconds = surface.heartbeatIntervalSeconds;
  media.traceCode = surface.watermark?.traceCode;
  media.releaseStarted = false;
  media.loading = false;
  media.fallbackDurationSeconds = activeVideoDurationFallback(root);
  media.virtualCurrentTime = undefined;
  media.lastProgressPercent = undefined;
  media.lastProgressSyncAt = undefined;
  media.resumeApplied = false;
	media.sourceGeneration = (media.sourceGeneration ?? 0) + 1;
	media.videoRecoveryAttempted = false;
  restoreVideoPosition(root, media);
  if (surface.watermark?.traceCode) updateVideoWatermarkTrace(root, surface.watermark.traceCode);
  syncMediaUi(root, media);
  return true;
}
